/** Получение ключей по образцу объекта */
function keysFromSample(sample) {
    return { keys: Object.keys(sample) };
}
class ConfigTools {
    /** Парсинг опций из DOM: data-options -> dataset (с типизацией). */
    static parseDomOptions(el, scheme) {
        // 1) data-options как StructuredData
        const raw = el.getAttribute('data-options');
        if (raw) {
            try {
                return ConfigTools.decodeValString(raw);
            }
            catch { /* fall through */ }
        }
        // 2) dataset с приведением типов
        const RESERVED = new Set(['widget', 'options']);
        const out = {};
        // lowercase -> canonical
        const canonByLc = new Map();
        if (scheme?.keys) {
            for (const key of scheme.keys) {
                const lc = key.toLowerCase();
                if (!canonByLc.has(lc))
                    canonByLc.set(lc, key); // первый выигрывает
            }
        }
        // Анализируем dataset на совпадение ключей с опциями виджета
        for (const [key, v] of Object.entries(el.dataset)) {
            var k = canonByLc.get(key.toLowerCase()) ?? key;
            if (RESERVED.has(k) || v == null)
                continue;
            // если ключ попал в scheme.records → декодируем как объект
            if (scheme?.records?.includes(k)) {
                out[k] = ConfigTools.decodeValString(v);
                continue;
            }
            const s = String(v).trim();
            const sl = s.toLowerCase();
            if (sl === 'true')
                out[k] = true;
            else if (sl === 'false')
                out[k] = false;
            else if (sl === 'null')
                out[k] = null;
            // else if (/^-?\d+(\.\d+)?$/.test(s)) out[k] = Number(s);
            else if (sl.startsWith('{') && sl.endsWith('}')) {
                try {
                    out[k] = JSON.parse(s);
                }
                catch (err) {
                    console.error('Не удалось распарсить как json', err);
                    out[k] = s;
                }
            }
            else
                out[k] = s;
        }
        return out;
    }
    /**
     * Разбирает viewKey формата "cfg.area.view" на три части.
     * @param viewKey Строка viewKey
     * @returns Объект с cfgName, areaName, viewName или null, если формат некорректен.
     */
    static parseViewKey(viewKey) {
        if (!viewKey)
            return null;
        const key = viewKey.trim().replace(/^['"]+|['"]+$/g, "");
        const parts = key.split(".");
        if (parts.length !== 3 || parts.some(p => !p.trim()))
            return null;
        const [cfgName, areaName, viewName] = parts;
        return { cfgName, areaName, viewName };
    }
    /**
     * Определяет, выглядит ли строка конфигурации как JSON.
     * JSON распознается по первому значимому символу; валидный StructuredText
     * всегда начинается с имени ключа.
     * @param raw Строка конфигурации
     */
    static looksLikeJson(raw) {
        if (!raw)
            return false;
        const c = raw.trimStart()[0];
        return c === '{' || c === '[' || c === '"';
    }
    /**
     * Разбирает строку data-cfg: строгий JSON либо legacy StructuredText.
     * Ошибка JSON не уходит в legacy-разбор.
     * @param raw Строка атрибута data-cfg
     */
    static decodeCfgString(raw) {
        if (!raw)
            return {};
        if (ConfigTools.looksLikeJson(raw)) {
            const parsed = JSON.parse(raw);
            if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed))
                throw new Error('Конфигурация view должна быть JSON-объектом');
            return parsed;
        }
        return ConfigTools.decodeValString(raw);
    }
    /**
     * Дозаполняет ViewOptions из строки data-cfg.
     * @param options Объект опций для изменения
     * @param dataCfg Строка атрибута data-cfg
     */
    static fillWithDataCfg(options, dataCfg) {
        if (!dataCfg || options.viewKey)
            return options;
        const parsed = this.decodeCfgString(dataCfg);
        // Определяем paserd.view
        const v = parsed.view;
        if (typeof v === "string" && v.trim()) {
            options.viewKey = v.trim();
        }
        return options;
    }
    /**
     * Извлекает viewKey из строки data-cfg.
     * Сначала пробует decodeCfgString (JSON/StructuredText), при ошибке — fallback на старый парсер.
     * @param dataCfg Строка атрибута data-cfg
     * @returns Строка viewKey или undefined
     */
    static extractViewKey(dataCfg) {
        if (!dataCfg)
            return undefined;
        try {
            const parsed = this.decodeCfgString(dataCfg);
            const v = parsed.view;
            if (typeof v === "string" && v.trim()) {
                return v.trim();
            }
        }
        catch {
            // fallback — ручной парсинг
        }
        // Старый алгоритм: ищем view= и вырезаем значение
        const idx = dataCfg.indexOf("view=");
        if (idx === -1)
            return undefined;
        // сдвигаем на view="..."
        let text = dataCfg.slice(idx + 5).trim();
        const quote = text[0];
        if (quote === '"' || quote === "'") {
            text = text.slice(1);
            const endIdx = text.indexOf(quote);
            if (endIdx !== -1) {
                return text.slice(0, endIdx).trim();
            }
        }
        return undefined;
    }
    /**
     * Кодирует ViewOptions в строку data-cfg (пока только viewKey).
     * Кодирование ведется в JSON; сервер принимает оба формата.
     */
    static encodeDataCfg(options) {
        // Обязательный параметр для кодирования опций
        if (!options.viewKey)
            return undefined;
        // Тут могут быть еще опции
        return JSON.stringify({ view: options.viewKey });
    }
    /**
     * Разбирает строку вида `key=value,key2="строка",key3=true` в объект с приведёнными типами.
     * Поддерживает:
     * - строки в двойных и одинарных кавычках с экранированием кавычек;
     * - булевы значения `true`/`false`;
     * - `null` и `dbnull`;
     * - числа (целые и дробные, с точкой или запятой, положительные и отрицательные);
     * - даты в формате `/.../` (возвращаются как Date).
     * @param stringData Строка значений
     * @returns Объект с ключами и значениями соответствующих типов
     */
    static decodeValString(stringData) {
        const result = {};
        if (!stringData)
            return result;
        const regEx = /\s*(\w+)\s*=\s*((?:"((?:[^"]|"")*)")|(?:'((?:[^']|'')*)')|true|false|null|dbnull|\d+\.\d+|\d+|\-\d+\.\d+|\-\d+|(?:\/((?:[^\/])*)\/))/g;
        stringData = stringData.replace(/[\n\r]/g, "");
        const matches = Array.from(stringData.matchAll(regEx));
        for (const it of matches) {
            let val;
            if (it[3] !== undefined) {
                val = it[3].replace(/""/g, '"');
            }
            else if (it[4] !== undefined) {
                val = it[4].replace(/''/g, "'");
            }
            else if (it[5] !== undefined) {
                val = new Date(it[5]);
            }
            else {
                const raw = it[2].toLowerCase();
                switch (raw) {
                    case "true":
                        val = true;
                        break;
                    case "false":
                        val = false;
                        break;
                    case "null":
                        val = null;
                        break;
                    case "dbnull":
                        val = "dbnull";
                        break;
                    default:
                        val = +it[2].replace(",", ".");
                        break;
                }
            }
            result[it[1]] = val;
        }
        return result;
    }
    /**
     * Кодирует объект в строку формата key=value,key2="строка".
     * Поддерживает типы: string, number, boolean, null, Date.
     * Для неподдерживаемых типов выполняется преобразование в строку.
     */
    static encodeValObject(values) {
        const parts = [];
        for (const [key, val] of Object.entries(values)) {
            let encoded;
            if (val === null) {
                encoded = "null";
            }
            else if (val === "dbnull") {
                encoded = "dbnull";
            }
            else if (typeof val === "boolean") {
                encoded = val ? "true" : "false";
            }
            else if (typeof val === "number" && isFinite(val)) {
                encoded = String(val);
            }
            else if (val instanceof Date) {
                encoded = `/${val.toISOString()}/`;
            }
            else {
                // всё остальное превращаем в строку с экранированием кавычек
                const str = String(val);
                encoded = `"${str.replace(/"/g, '""')}"`;
            }
            parts.push(`${key}=${encoded}`);
        }
        return parts.join(",");
    }
}

export { ConfigTools, keysFromSample };
